import { useHeatmapClickRankSearch, useHeatmapLoadingContext } from '~/modules/heatmap';
import { useHeatmapClickContext, useHeatmapVizContext } from '@gem-sdk/heatmap-react';
import { GEmptySearch, GSkeletonDisplayText } from '~/presentation/common';
import { BlockStack, Box, InlineStack } from '@shopify/polaris';
import { EHeatmapLoadingKey } from '~/modules/heatmap/types';
import { useEffect, useRef, useCallback, memo } from 'react';
import { GBlockCenter } from '~/presentation/ui';
import { ClickRankItem } from './ClickRankItem';
import { useTranslation } from 'react-i18next';
import { SPACING_INSET } from '../Sidebar';

const SKELETON_ITEM_COUNT = 3;

interface ClickRankProps {
  isScrolled: boolean;
}

export const ClickRank: React.FC<ClickRankProps> = ({ isScrolled }) => {
  const isDomLoaded = useHeatmapVizContext((s) => s.isDomLoaded);
  const isLoading = useHeatmapLoadingContext((s) =>
    s.getLoadingByKey(EHeatmapLoadingKey.CLICK_ELEMENT),
  );

  const { filteredElements, isEmptySearch } = useHeatmapClickRankSearch();

  const elementRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  const setElementRef = useCallback(
    (hash: string) => (node: HTMLDivElement | null) => {
      if (node) {
        elementRefs.current.set(hash, node);
      } else {
        elementRefs.current.delete(hash);
      }
    },
    [],
  );

  if (!isDomLoaded || isLoading) {
    return <RankSkeleton />;
  }

  return (
    <>
      <AutoScrollHandler elementRefs={elementRefs} />
      {isScrolled && (
        <div
          className="gx-hm-divider"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            width: '100%',
          }}
        />
      )}
      {isEmptySearch ? (
        <RankEmptySearch />
      ) : (
        <Box padding={SPACING_INSET} paddingBlockStart="025" paddingInlineEnd="0">
          <BlockStack gap={SPACING_INSET}>
            {filteredElements?.map((item) => (
              <ClickRankItem
                key={item.hash}
                rank={item.rank}
                item={item}
                ref={setElementRef(item.hash)}
              />
            ))}
          </BlockStack>
        </Box>
      )}
    </>
  );
};

// ============================ //
// SubComponents
// ============================ //

const AutoScrollHandler: React.FC<{ elementRefs: React.RefObject<Map<string, HTMLDivElement>> }> =
  memo(({ elementRefs }) => {
    const selectedElement = useHeatmapClickContext((s) => s.selectedElement);

    useEffect(() => {
      const selectedHash = selectedElement?.hash;
      if (selectedHash && elementRefs.current) {
        const elementNode = elementRefs.current.get(selectedHash);
        if (elementNode) {
          elementNode.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          });
        }
      }
    }, [selectedElement, elementRefs]);

    return null;
  });
AutoScrollHandler.displayName = 'AutoScrollHandler';

const RankEmptySearch: React.FC = () => {
  const { t } = useTranslation();

  return (
    <GBlockCenter paddingBlock="100" paddingBlockEnd="300">
      <GEmptySearch heading={t('No result found')} description={t('Try another search query')} />
    </GBlockCenter>
  );
};

const RankSkeleton: React.FC = () => (
  <Box padding={SPACING_INSET} paddingBlockStart="025" paddingInlineEnd="0">
    <BlockStack gap={SPACING_INSET}>
      {Array.from({ length: SKELETON_ITEM_COUNT }).map((_, index) => (
        <RankItemSkeleton key={index} />
      ))}
    </BlockStack>
  </Box>
);

const RankItemSkeleton: React.FC = () => (
  <div className="Heatmap-Ranked_Item">
    <Box padding="300">
      <InlineStack blockAlign="start" gap="200" wrap={false}>
        <div
          style={{
            width: '20px',
            height: '20px',
            flexShrink: 0,
            borderRadius: '100%',
            overflow: 'hidden',
          }}
        >
          <GSkeletonDisplayText height="20px" />
        </div>
        <BlockStack gap="100">
          <Box padding="100">
            <div style={{ width: '64px' }}>
              <GSkeletonDisplayText height="12px" />
            </div>
          </Box>
          <Box padding="100">
            <div style={{ width: '84px' }}>
              <GSkeletonDisplayText height="12px" />
            </div>
          </Box>
        </BlockStack>
      </InlineStack>
    </Box>
  </div>
);
